import React, { useEffect, useState, createElement, Component } from 'react';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
import { ExpertProfile } from '../components/expertsData';
import { CVRequestModal } from '../components/CVRequestModal';
import { ShieldCheck, Mail, CheckCircle, FileText, Stethoscope, Award, FileDown, Clock, Scale, Briefcase } from 'lucide-react';
interface ExpertDetailPageProps {
  expert: ExpertProfile;
}
export function ExpertDetailPage({
  expert
}: ExpertDetailPageProps) {
  const [showCVModal, setShowCVModal] = useState(false);
  useEffect(() => {
    document.title = `${expert.name} | ${expert.role} | UK Medicolegal Assessment`;
    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('name', name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };
    setMeta('description', `${expert.name} is a ${expert.role} with ${expert.experience} experience. GMC: ${expert.gmc}. ${expert.subheadline}. Instruct today.`);
    setMeta('robots', 'index, follow');
    // Canonical URL for SEO
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = `https://ukmedicolegal.co.uk/experts/${expert.slug}`;
  }, [expert]);
  const EMAIL = 'info@uk-medicolegal-assessment.com';
  const SUBJECT = encodeURIComponent(`Expert Instruction: ${expert.name}`);
  const BODY = encodeURIComponent(`Dear UK Medicolegal Assessment,

I would like to instruct ${expert.name} (${expert.role}). Please find the case details below:

Full Name: 
Organisation / Law Firm: 
Claimant Name: 
Case Type: 
Brief Case Summary: 
Urgency / Preferred Timescale: 
Any Specific Questions for the Expert: 

Please confirm availability and provide a fee quote.

Kind regards,
`);
  const MAILTO_LINK = `mailto:${EMAIL}?subject=${SUBJECT}&body=${BODY}`;
  // JSON-LD Schema
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Person',
    name: expert.name,
    jobTitle: expert.role,
    worksFor: {
      '@type': 'Organization',
      name: 'UK Medicolegal Assessment'
    },
    description: expert.bio,
    knowsAbout: expert.clinicalExpertise,
    url: `https://ukmedicolegal.co.uk/experts/${expert.slug}`
  };
  return <div className="min-h-screen bg-warm-50 pb-24 lg:pb-0" data-id="element-761">
      <script type="application/ld+json" data-id="element-762">{JSON.stringify(schema)}</script>
      <Navigation data-id="element-763" />

      <main className="pt-32 pb-20 md:pt-40 md:pb-24" data-id="element-764">
        <div className="max-w-5xl mx-auto px-5 sm:px-6 lg:px-8" data-id="element-765">
          {/* Top Section: Portrait & Summary */}
          <div className="flex flex-col md:flex-row gap-8 md:gap-12 mb-12" data-id="element-766">
            {/* Left: Portrait */}
            <div className="w-full md:w-1/3 flex-shrink-0" data-id="element-767">
              <div className="aspect-square md:aspect-[3/4] rounded-2xl overflow-hidden bg-warm-100 border border-warm-200" data-id="element-768">
                <img src={expert.image} alt={`Professional headshot of ${expert.name}, ${expert.role} for UK medico-legal expert witness services`} className="w-full h-full object-cover grayscale" loading="eager" data-id="element-769" />
              </div>
            </div>

            {/* Right: Summary */}
            <div className="w-full md:w-2/3 flex flex-col justify-center" data-id="element-770">
              <div className="mb-6" data-id="element-771">
                <h1 className="text-3xl md:text-4xl lg:text-[2.75rem] font-bold text-ink-900 tracking-tight mb-1.5" data-id="element-772">
                  {expert.name}
                </h1>
                <p className="text-xl text-teal-600 font-bold mb-1" data-id="element-773">
                  {expert.role}
                </p>
                <p className="text-base text-ink-500 font-medium mb-4" data-id="element-774">
                  {expert.subheadline}
                </p>

                <div className="flex flex-wrap items-center gap-3 mb-6" data-id="element-775">
                  <div className="inline-flex items-center text-sm font-mono font-bold text-ink-900 bg-warm-100 px-3.5 py-2 rounded-xl border border-warm-200" role="img" aria-label={`Official General Medical Council registration badge indicating verified clinical status — GMC number ${expert.gmc}`} data-id="element-776">
                    <ShieldCheck className="h-4 w-4 mr-2 text-sage-500" aria-hidden="true" data-id="element-777" />
                    GMC: {expert.gmc}
                  </div>
                  <span className="text-sm text-ink-500 font-medium" data-id="element-778">
                    {expert.qualifications}
                  </span>
                </div>
              </div>

              {/* Key Stats — the 3 things solicitors need instantly */}
              <div className="bg-white border border-[#E2E8F0] rounded-2xl p-6 space-y-4" data-id="element-779">
                <div className="flex items-start gap-3" data-id="element-780">
                  <Scale className="h-4 w-4 text-teal-600 mt-0.5 flex-shrink-0" data-id="element-781" />
                  <div data-id="element-782">
                    <p className="text-xs font-bold text-ink-400 uppercase tracking-wider" data-id="element-783">
                      Instruction Ratio
                    </p>
                    <p className="text-ink-900 font-semibold" data-id="element-784">
                      {expert.instructionRatio}
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3" data-id="element-785">
                  <Clock className="h-4 w-4 text-teal-600 mt-0.5 flex-shrink-0" data-id="element-786" />
                  <div data-id="element-787">
                    <p className="text-xs font-bold text-ink-400 uppercase tracking-wider" data-id="element-788">
                      Turnaround
                    </p>
                    <p className="text-ink-900 font-semibold" data-id="element-789">
                      {expert.turnaround}
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3" data-id="element-790">
                  <CheckCircle className="h-4 w-4 text-teal-600 mt-0.5 flex-shrink-0" data-id="element-791" />
                  <div data-id="element-792">
                    <p className="text-xs font-bold text-ink-400 uppercase tracking-wider" data-id="element-793">
                      Compliance
                    </p>
                    <p className="text-ink-900 font-semibold" data-id="element-794">
                      {expert.compliance}
                    </p>
                  </div>
                </div>
              </div>

              {/* Secondary stats */}
              <div className="grid grid-cols-3 gap-4 mt-4" data-id="element-795">
                <div className="bg-white p-3 rounded-xl border border-[#E2E8F0] text-center" data-id="element-796">
                  <p className="text-xs font-bold text-ink-400 uppercase tracking-wider mb-0.5" data-id="element-797">
                    Experience
                  </p>
                  <p className="text-ink-900 font-bold" data-id="element-798">{expert.experience}</p>
                </div>
                <div className="bg-white p-3 rounded-xl border border-[#E2E8F0] text-center" data-id="element-799">
                  <p className="text-xs font-bold text-ink-400 uppercase tracking-wider mb-0.5" data-id="element-800">
                    Reports
                  </p>
                  <p className="text-ink-900 font-bold" data-id="element-801">
                    {expert.reportsCompleted}
                  </p>
                </div>
                <div className="bg-white p-3 rounded-xl border border-[#E2E8F0] text-center" data-id="element-802">
                  <p className="text-xs font-bold text-ink-400 uppercase tracking-wider mb-0.5" data-id="element-803">
                    NHS Post
                  </p>
                  <p className="text-ink-900 font-bold text-xs leading-tight" data-id="element-804">
                    {expert.nhsPost}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Clinical & Legal Focus — Spartan summary */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12" data-id="element-805">
            <div className="bg-white p-6 rounded-2xl border border-[#E2E8F0]" data-id="element-806">
              <h2 className="text-sm font-bold text-ink-400 uppercase tracking-wider mb-3 flex items-center" data-id="element-807">
                <Stethoscope className="h-4 w-4 mr-2 text-teal-600" data-id="element-808" />
                Clinical Focus
              </h2>
              <p className="text-ink-700 leading-relaxed" data-id="element-809">
                {expert.clinicalFocus}
              </p>
            </div>
            <div className="bg-white p-6 rounded-2xl border border-[#E2E8F0]" data-id="element-810">
              <h2 className="text-sm font-bold text-ink-400 uppercase tracking-wider mb-3 flex items-center" data-id="element-811">
                <Briefcase className="h-4 w-4 mr-2 text-teal-600" data-id="element-812" />
                Medico-Legal Expertise
              </h2>
              <p className="text-ink-700 leading-relaxed" data-id="element-813">
                {expert.medicoLegalExpertise}
              </p>
            </div>
          </div>

          {/* Middle Section: Detailed Expertise & Training */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12" data-id="element-814">
            <div className="bg-white p-8 rounded-2xl border border-[#E2E8F0]" data-id="element-815">
              <h2 className="text-lg font-bold text-ink-900 mb-6 flex items-center" data-id="element-816">
                <Stethoscope className="h-5 w-5 mr-2.5 text-teal-600" data-id="element-817" />
                Clinical Expertise
              </h2>
              <ul className="space-y-2.5" data-id="element-818">
                {expert.clinicalExpertise.map((item, index) => <li key={index} className="flex items-start text-ink-600" data-id="element-819">
                    <span className="text-teal-500 mr-2.5 mt-1" data-id="element-820">•</span>
                    <span className="leading-relaxed" data-id="element-821">{item}</span>
                  </li>)}
              </ul>
            </div>

            <div className="bg-white p-8 rounded-2xl border border-[#E2E8F0]" data-id="element-822">
              <h2 className="text-lg font-bold text-ink-900 mb-6 flex items-center" data-id="element-823">
                <Award className="h-5 w-5 mr-2.5 text-teal-600" data-id="element-824" />
                Medico-Legal Training
              </h2>
              <ul className="space-y-2.5" data-id="element-825">
                {expert.medicoLegalTraining.map((item, index) => <li key={index} className="flex items-start text-ink-600" data-id="element-826">
                    <span className="text-teal-500 mr-2.5 mt-1" data-id="element-827">•</span>
                    <span className="leading-relaxed" data-id="element-828">{item}</span>
                  </li>)}
              </ul>
            </div>
          </div>

          {/* Bio & Case Types */}
          <div className="mb-12" data-id="element-829">
            <h2 className="text-2xl font-bold text-ink-900 mb-6" data-id="element-830">
              Professional Profile
            </h2>
            <p className="text-lg text-ink-600 leading-relaxed mb-8" data-id="element-831">
              {expert.bio}
            </p>

            <div className="mb-6" data-id="element-832">
              <h3 className="text-sm font-bold text-ink-400 uppercase tracking-wider mb-3" data-id="element-833">
                Instructed Case Types
              </h3>
              <div className="flex flex-wrap gap-2" data-id="element-834">
                {expert.caseTypes.map((type, index) => <span key={index} className="inline-flex items-center px-3.5 py-1.5 rounded-full text-sm font-medium bg-warm-100 text-ink-700 border border-warm-200" data-id="element-835">
                    {type}
                  </span>)}
              </div>
            </div>
          </div>

          {/* Bottom Section: Instruction Box with dual CTAs */}
          <div className="bg-white rounded-2xl border border-[#E2E8F0] overflow-hidden" data-id="element-836">
            <div className="p-8 md:p-12" data-id="element-837">
              <h2 className="text-2xl md:text-3xl font-bold text-ink-900 mb-3" data-id="element-838">
                Instruct {expert.name}
              </h2>
              <p className="text-ink-500 leading-relaxed mb-8 max-w-xl" data-id="element-839">
                Send us your instruction and we will confirm availability,
                provide a fee quote, and arrange the assessment within 24 hours.
                Mark "Urgent" in the subject for priority response.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 mb-8" data-id="element-840">
                <a href={MAILTO_LINK} className="inline-flex items-center justify-center bg-teal-500 hover:bg-teal-600 active:bg-teal-700 text-white rounded-xl py-4 px-8 font-semibold shadow-sm transition-colors duration-200 text-base min-h-[52px] w-full sm:w-auto" data-id="element-841">
                  <Mail className="mr-2.5 h-5 w-5" data-id="element-842" />
                  Instruct Expert
                </a>
                <button onClick={() => setShowCVModal(true)} className="inline-flex items-center justify-center px-8 py-4 text-base font-semibold rounded-xl border border-ink-300 text-ink-600 hover:border-teal-500 hover:text-teal-600 transition-colors duration-200 min-h-[52px] w-full sm:w-auto" data-id="element-843">
                  <FileDown className="mr-2.5 h-5 w-5" data-id="element-844" />
                  Request CV
                </button>
              </div>

              <div className="flex flex-wrap gap-x-6 gap-y-3 text-sm text-ink-500 font-medium" data-id="element-845">
                <span className="flex items-center" data-id="element-846">
                  <ShieldCheck className="h-4 w-4 mr-1.5 text-sage-500" data-id="element-847" />
                  ISO 27001 Certified
                </span>
                <span className="flex items-center" data-id="element-848">
                  <CheckCircle className="h-4 w-4 mr-1.5 text-sage-500" data-id="element-849" />
                  GDPR Compliant
                </span>
                <span className="flex items-center" data-id="element-850">
                  <FileText className="h-4 w-4 mr-1.5 text-sage-500" data-id="element-851" />
                  CPR Part 35 Compliant
                </span>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer data-id="element-852" />

      {/* Mobile Sticky CTA */}
      <div className="fixed bottom-0 left-0 right-0 p-4 bg-white/90 backdrop-blur-md border-t border-warm-200 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)] z-50 lg:hidden" data-id="element-853">
        <a href={MAILTO_LINK} className="btn-primary w-full text-center py-3.5 text-base shadow-sm" data-id="element-854">
          <Mail className="mr-2 h-5 w-5" data-id="element-855" />
          Instruct this Expert
        </a>
      </div>

      <CVRequestModal expertName={expert.name} expertRole={expert.role} isOpen={showCVModal} onClose={() => setShowCVModal(false)} data-id="element-856" />
    </div>;
}